import React, { useMemo } from "react";
import { useField } from "formik";
import { uniqBy } from "lodash/array";
import { useDispatch, useSelector } from "react-redux";
import { v4 as uuidv4 } from "uuid";
import { updateDeploymentAttributes } from "actions";
import { UPDATE_DEPLOYMENT_LABELS } from "constants/actionTypes";
import {
  getCurrentSpeciesId,
  getDeployments,
  getIsSaleyardAdmin,
  getLabels,
  selectCurrentDeploymentIds,
} from "selectors";
import { LabelSelectorField } from "./LabelSelectorField";

interface ManualChargeLabelSelectorFieldProps {
  name: string;
  label: string;
  tooltip?: string;
  disabled?: boolean;
  required?: boolean;
}

export const ManualChargeLabelSelectorField = ({
  name,
  label,
  tooltip,
  disabled,
  required,
}: ManualChargeLabelSelectorFieldProps) => {
  const dispatch = useDispatch();

  const handleUpdate = payload =>
    dispatch(updateDeploymentAttributes(UPDATE_DEPLOYMENT_LABELS, payload));

  const isSaleyardAdmin: boolean = useSelector(getIsSaleyardAdmin);
  const deployments: Deployment[] = useSelector(getDeployments);

  const currentSpeciesId: number = useSelector(getCurrentSpeciesId);

  // On create, create for all available deployments.  On Select, if there are duplicates, select the first one.
  // This is not ideal, and would be nicer if Labels weren't DeploymentLabels.
  const currentDeploymentIds: number[] = useSelector(
    selectCurrentDeploymentIds,
  );

  const labels: DeploymentLabel[] = useSelector(getLabels);

  const [{ value: labelsInUse = [] }] = useField(name);

  const options = useMemo(
    () =>
      uniqBy(
        Object.values(labels).filter(
          deploymentLabel =>
            labelsInUse.includes(deploymentLabel.id) ||
            (currentDeploymentIds.includes(deploymentLabel.deployment_id) &&
              deploymentLabel.is_manual_charge_label &&
              deploymentLabel.species_id === currentSpeciesId &&
              !deploymentLabel.deleted &&
              !deploymentLabel.deprecated),
        ),
        "id",
      ).map(option => ({
        label: isSaleyardAdmin
          ? `${deployments[option.deployment_id]?.name}: ${option.name}`
          : option.name,
        value: option.id,
      })),
    [
      currentDeploymentIds,
      currentSpeciesId,
      deployments,
      isSaleyardAdmin,
      labels,
      labelsInUse,
    ],
  );

  const addLabel = valueLabel => {
    currentDeploymentIds.forEach(deploymentId => {
      const payload = {
        order: labels.length,
        deployment_id: deploymentId,
        name: valueLabel,
        species_id: currentSpeciesId,
        is_used: true,
        id: uuidv4(),
        quick_select: false,
        is_manual_charge_label: true,
        deprecated: false,
      };
      handleUpdate([payload]);
    });
  };

  return (
    <LabelSelectorField
      name={name}
      label={label}
      tooltip={tooltip}
      required={required}
      disabled={disabled}
      addLabel={addLabel}
      options={options}
      placeHolder="Define the reason for this sundry."
    />
  );
};
